import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ABTest } from '@freelancer/abtest';
import {
  BackendPushErrorResponse,
  BackendUpdateErrorResponse,
  Datastore,
} from '@freelancer/datastore';
import {
  Bid,
  BidBuyerProjectFeesCollection,
  BidsCollection,
  CartsCollection,
  MilestoneDraftsCollection,
  ProjectViewProject,
  ProjectViewUser,
} from '@freelancer/datastore/collections';
import { MilestoneCart } from '@freelancer/milestone-cart';
import {
  PartialPaymentsCartItem,
  PaymentsCart,
} from '@freelancer/payments-cart';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { ContextTypeApi, DestinationApi } from 'api-typings/payments/payments';
import {
  BidAwardStatusApi,
  ProjectTypeApi,
} from 'api-typings/projects/projects';
import * as Rx from 'rxjs';
import { take } from 'rxjs/operators';
import { HourlyAwardModalErrorType } from '../hourly-award-modal-error/hourly-award-modal-error.component';

export interface AwardForm {
  withMilestone: boolean;
  projectBudgetAmount: number;
  projectDays: number;
  milestoneAmount: number;
  milestoneDescription: string;
}

enum ProjectFeeType {
  MILESTONE = 1,
  HOURLY_INITIAL_PAYMENT = 2,
  FIXED_PRICE_PROJECT = 3,
}

@Component({
  selector: 'app-hourly-fixed-award-modal',
  template: `
    <app-hourly-award-layout>
      <app-hourly-award-as-fixed-form
        hourly-award-layout-body
        [projectCurrency]="project?.currency"
        [allowMilestoneCreation]="allowMilestoneCreation"
        [submissionPromise]="submissionPromise"
        [bid]="bid"
        [project]="project"
        [user]="user"
        [isBusy]="isBusy"
        [isProjectFeeChargedOnAward]="isProjectFeeChargedOnAward$ | flAsync"
        (switchToHourly)="switchToHourly()"
        (award)="award($event)"
      ></app-hourly-award-as-fixed-form>
      <app-award-modal-sidebar
        hourly-award-layout-sidebar
      ></app-award-modal-sidebar>
    </app-hourly-award-layout>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HourlyAwardAsFixedComponent implements OnInit {
  @Input() bid: Bid;
  @Input() project: ProjectViewProject;
  @Input() user: ProjectViewUser;
  @Input() allowMilestoneCreation: boolean;
  @Output() switchAwardType = new EventEmitter<ProjectTypeApi>();
  @Output() close = new EventEmitter<boolean>();

  // Charge project fee on award T233747
  @Input() escrowRequired: boolean;
  isProjectFeeChargedOnAward$: Rx.Observable<boolean>;

  submissionPromise: Promise<
    (
      | BackendUpdateErrorResponse<BidsCollection>
      | BackendPushErrorResponse<MilestoneDraftsCollection>
      | BackendPushErrorResponse<CartsCollection>
    ) & {
      errorType: HourlyAwardModalErrorType;
    }
  >;
  isAwardSuccess = false;
  isBusy = false;

  constructor(
    private datastore: Datastore,
    private paymentsCart: PaymentsCart,
    private milestoneCart: MilestoneCart,
    // Charge project fee on award T233747
    private abtest: ABTest,
  ) {}

  // Charge project fee on award T233747
  ngOnInit() {
    this.isProjectFeeChargedOnAward$ = this.showChargeProjectFeeOnAwardVariant();
  }

  switchToHourly() {
    this.switchAwardType.emit(ProjectTypeApi.HOURLY);
  }

  async award(awardForm: AwardForm) {
    // Keep the same logic as the old pvp that
    // when the freelancer is awarded then navigate to payments tab
    // if the button is clicked again.
    if (this.isAwardSuccess) {
      this.navigateToPaymentTab();
      return;
    }

    // Charge project fee on award T233747
    const isProjectFeeChargedOnAward = await this.isProjectFeeChargedOnAward$
      .pipe(take(1))
      .toPromise();

    if (isProjectFeeChargedOnAward) {
      this.isBusy = true;

      if (!awardForm.withMilestone) {
        const projectFee = await this.datastore
          .document<BidBuyerProjectFeesCollection>(
            'bidBuyerProjectFees',
            this.bid.id,
          )
          .valueChanges()
          .pipe(take(1))
          .toPromise();

        const projectFeeCartItem: PartialPaymentsCartItem[] = [
          {
            contextType: ContextTypeApi.PROJECT_FEE,
            contextId: `${this.bid.id}`,
            description: 'Project Fee',
            currencyId: this.project.currency.id,
            amount: projectFee.amount,
            useBonus: false,
            contextSubType: ProjectFeeType.FIXED_PRICE_PROJECT,
          },
        ];

        const description = `Project fee of project ${this.project.id}`;
        return this.paymentsCart
          .handle(
            description,
            {
              destination: DestinationApi.PROJECT_VIEW_PAGE,
              payload: `${this.project.id}`,
            },
            projectFeeCartItem,
          )
          .then(() => (this.isBusy = false));
      }

      const milestoneDraftResponse = await this.createMilestoneDraft(
        awardForm,
        this.project,
        this.bid,
      );

      if (milestoneDraftResponse.status !== 'success') {
        throw milestoneDraftResponse;
      }

      // FIXME: investigate project fees regarding award hourly as fixed T238280
      const cartItems = await this.milestoneCart.createMilestoneCartItem({
        milestoneDraftId: milestoneDraftResponse.id,
        onAward: true,
        shadowFlag: isProjectFeeChargedOnAward,
      });

      const description = `Milestones and project fee of project ${this.project.id}`;
      return this.paymentsCart
        .handle(
          description,
          {
            destination: DestinationApi.PROJECT_VIEW_PAGE,
            payload: `${this.project.id}`,
          },
          cartItems,
        )
        .then(() => (this.isBusy = false));
    }

    this.submissionPromise = this.datastore
      .document<BidsCollection>('bids', this.bid.id)
      .update({
        awardStatus: BidAwardStatusApi.PENDING,
        amount: awardForm.projectBudgetAmount,
        period: awardForm.projectDays,
        extraForUpdate: {
          skipHourlyContract: true,
        },
      })
      .then(async awardResponse => {
        if (awardResponse.status !== 'success') {
          switch (awardResponse.errorCode) {
            case ErrorCodeApi.FREELANCER_ACCOUNT_UNDER_REVIEW: {
              const e = {
                ...awardResponse,
                errorType:
                  HourlyAwardModalErrorType.FREELANCER_ACCOUNT_UNDER_REVIEW,
              };
              throw e;
            }
            case ErrorCodeApi.MAXIMUM_AWARDED_BIDDERS_REACHED: {
              const e = {
                ...awardResponse,
                errorType:
                  HourlyAwardModalErrorType.MAXIMUM_AWARDED_BIDDERS_REACHED,
              };
              throw e;
            }
            case ErrorCodeApi.FREELANCER_ALREADY_AWARDED: {
              const e = {
                ...awardResponse,
                errorType: HourlyAwardModalErrorType.FREELANCER_ALREADY_AWARDED,
              };
              throw e;
            }
            default: {
              const e = {
                ...awardResponse,
                errorType: HourlyAwardModalErrorType.AWARD_FAILED,
              };
              throw e;
            }
          }
        }

        this.isAwardSuccess = true;

        if (!awardForm.withMilestone) {
          return this.navigateToPaymentTab();
        }

        const milestoneDraftResponse = await this.createMilestoneDraft(
          awardForm,
          this.project,
          this.bid,
        );
        if (milestoneDraftResponse.status !== 'success') {
          throw milestoneDraftResponse;
        }

        // FIXME: investigate project fees regarding award hourly as fixed T238280
        const cartItems = await this.milestoneCart.createMilestoneCartItem({
          milestoneDraftId: milestoneDraftResponse.id,
          onAward: true,
          shadowFlag: false,
        });
        const description = `Milestones of project ${this.project.id}`;
        return this.paymentsCart.handle(
          description,
          {
            destination: DestinationApi.PROJECT_VIEW_PAGE,
            payload: `${this.project.id}`,
          },
          cartItems,
        );
      })
      .catch(e => ({
        errorType: HourlyAwardModalErrorType.MILESTONE_FAILED,
        ...e, // in case of AWARD_FAILED it overwrites the MILESTONE_FAILED
      }));
  }

  createMilestoneDraft(
    awardForm: AwardForm,
    project: ProjectViewProject,
    bid: Bid,
  ) {
    return this.datastore.createDocument<MilestoneDraftsCollection>(
      'milestoneDrafts',
      {
        amount: Number(awardForm.milestoneAmount),
        bidderId: bid.bidderId,
        bidId: bid.id,
        currencyId: project.currency.id,
        description: awardForm.milestoneDescription,
        milestoneRequestId: undefined,
        projectId: project.id,
        projectOwnerId: project.ownerId,
        timeCreated: Date.now(),
        transactionId: undefined,
      },
    );
  }

  navigateToPaymentTab() {
    // close modal and navigate to payment tab
    this.close.emit(true);
  }

  // Charge project fee on award T233747
  showChargeProjectFeeOnAwardVariant(): Rx.Observable<boolean> {
    // removing hourly-fixed projects from the experiment and releasing it in shadow for now
    const newUsersMinDate = new Date('2021-08-27 02:00:00+00:00').getTime();

    if (
      // Do not enrol non-rookie user
      this.user.registrationDate < newUsersMinDate ||
      // Do not enrol non-english speaking user
      !this.user.primaryLanguage ||
      this.user.primaryLanguage !== 'en' ||
      // Do not enrol cali project
      this.escrowRequired ||
      // Do not enrol if enterprise project.
      (!!this.project.enterpriseIds && this.project.enterpriseIds.length > 0) ||
      (!!this.user.enterpriseIds && this.user.enterpriseIds.length > 0) ||
      // Do not enrol hire me project
      this.project.hireme
      // md5 check to enrol 20% of users
      // !this.abtest.shouldEnrol(
      //   'T233747-charge-project-fee-on-award',
      //   this.project.ownerId,
      //   0.2,
      // )
    ) {
      return Rx.of(false);
    }

    // Exclude the user from enrollment when whitelist cookie is set
    // while still showing them the test variant.
    if (this.abtest.isWhitelistUser()) {
      return Rx.of(true); // Test variant.
    }

    // return this.abtest
    //   .getUserExperimentVariation(
    //     'T233747-charge-project-fee-on-award',
    //     this.project.ownerId,
    //   )
    //   .pipe(map(variation => variation === 'test'));
    return Rx.of(false);
  }
}
